import * as braze from '@braze/web-sdk';
import { Braze } from '@foxcorp/cpe-commerce-web';
import { getDefaultConfig, getState } from 'Utils/Store';
import { BRAZE_ZONES, MY_ACCOUNT_TOP, MY_ACCOUNT_2ND, CANCEL_INTERRUPT } from 'Utils/Constants';
import { setContentCards } from 'State/ContentCards';
import { createApiTokenHandler } from './Api/RefreshTokenHandler';
import { logError } from './Logger';
import { setLocation } from './Path';

export const setBrazeUserLoggedIn = (loggedIn) => braze.getUser().setCustomUserAttribute('u_log_stat_web', loggedIn);

export const setBrazeUser = (profileId) => {
  const {
    user: { isAuthenticated },
  } = getState();
  const { nodeEnv } = getDefaultConfig();
  braze.changeUser(profileId);
  if (nodeEnv === 'development') {
    braze.requestImmediateDataFlush();
  }
  setBrazeUserLoggedIn(!!isAuthenticated);
};

// initialize the SDK
export const initBraze = (profileId) => {
  const { brazeApiKey, enableBrazeLogging } = getDefaultConfig();
  braze.initialize(brazeApiKey, {
    baseUrl: 'sdk.iad-05.braze.com',
    // logging based on environment
    enableLogging: enableBrazeLogging,
    allowUserSuppliedJavascript: true,
    minimumIntervalBetweenTriggerActionsInSeconds: 0,
  });

  // set user for braze session
  const brazeUser = profileId;
  if (brazeUser) {
    setBrazeUser(brazeUser);
  }

  // init braze content cards (banners)
  braze.requestContentCardsRefresh();
  braze.subscribeToContentCardsUpdates((content) => {
    const sortedContentCards = content.cards.sort((a, b) => Date.parse(b.updated) - Date.parse(a.updated));
    const filteredContentCards = BRAZE_ZONES.map((zone) => {
      // 1. Find all content cards in zone
      // 2. Return prioritized content card using prioritize KVP in extras settings
      // 3. If not, return first one in sorted cards
      const allCardsInZone = sortedContentCards.filter((banner) => banner.extras.zone === zone);
      const prioritizedCard = allCardsInZone.find((card) => card.extras?.prioritize === 'true');
      const latestCard = allCardsInZone.find((banner) => banner.extras.zone === zone);
      return prioritizedCard ? prioritizedCard : latestCard;
    }).filter((item) => item !== undefined);
    setContentCards(filteredContentCards);
  });

  // init braze session
  braze.openSession();

  initBrazeActions();

  initBrazeIAMHandler();
};

const initBrazeActions = () => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  Braze.createActions({
    apiKey: accountApiKey,
    apiUrl: foxApi,
  });
};

// we want certain IAMs to display immediately back-to-back (e.g. cancel plan offer followed by success/error)
// therefore we set `minimumIntervalBetweenTriggerActionsInSeconds` to 0 and implement our own cooldown logic
// such that IAMs on Braze with the key-value pair of `exemptFromCooldown: true` are not subject to this cooldown
let lastInAppMessageTime = 0;
const BRAZE_IAM_COOLDOWN = 30 * 1000; // 30 seconds

const canShowInAppMessage = (inAppMessage) => {
  const exemptFlag = inAppMessage.extras?.exemptFromCooldown;
  if (exemptFlag === 'true') {
    return true;
  }

  const now = Date.now();
  const timeSinceLastMessage = now - lastInAppMessageTime;
  return timeSinceLastMessage >= BRAZE_IAM_COOLDOWN;
};

const markInAppMessageShown = () => {
  lastInAppMessageTime = Date.now();
};

const initBrazeIAMHandler = () => {
  // subscribe to each braze IAM and display them
  braze.subscribeToInAppMessage(function (inAppMessage) {
    if (!canShowInAppMessage(inAppMessage)) {
      console.info('Braze: in-app message blocked by manual cooldown');
      return;
    }

    inAppMessage.buttons?.forEach((button) => {
      button.subscribeToClickedEvent(function () {
        const { applyCoupon, upgradeSubscription, downgradeSubscription, actionButtonText, customEvent } =
          inAppMessage.extras ?? {};
        if (button.text === actionButtonText) {
          (async () => {
            try {
              if (applyCoupon || upgradeSubscription || downgradeSubscription) {
                // wrap Braze actions in token handler so device registration/refresh token logic is applied
                await createApiTokenHandler(() =>
                  Braze.contentCardClickAction({
                    extras: { applyCoupon, upgradeSubscription, downgradeSubscription },
                  })
                );
              }
              braze.logCustomEvent(customEvent, { status: 'success' });
              setLocation('/account/manage');
            } catch (error) {
              logError(error, 'Braze IAM Click Error');
              braze.logCustomEvent(customEvent, { status: 'error' });
            }
          })();
        }
      });
    });

    markInAppMessageShown();
    // display the iam
    braze.showInAppMessage(inAppMessage);
  });
};

// Checks for an existing content card to display in the zone
export const hasBrazeContentCard = (zone) => {
  const contentCards = getState('contentCards').allContentCards;
  return contentCards.some((contentCard) => contentCard.extras.zone === zone);
};

// Braze listens on a custom card removal events and remove the banner
export const getCustomEvent = (zone) => {
  switch (zone) {
    case MY_ACCOUNT_TOP:
      return 'My_Account_Web_Banner_Click';
    case MY_ACCOUNT_2ND:
      return 'My_Account_Web_Banner_Click';
    case CANCEL_INTERRUPT:
      return 'Cancel_Interrupt_Web_Banner_Click';
    default:
      break;
  }
};

export const getContentCardByZone = (contentCards, zone) => {
  return contentCards.filter((contentCard) => contentCard.extras.zone === zone)[0];
};

// returns the path minus the last part of the path & replacing it with "/*"
export const getPathRoot = (path) => {
  const pathRoot = path.replace(/\/[^/]*\/?$/, '/*');
  return pathRoot;
};

export const getContentCardCampaignType = (contentCard) => contentCard?.extras.campaign_type;
